import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUtils';
import SimpleLoader from './SimpleLoader';


const PublicBrowse = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState({
    itemType: '',
    listingType: '',
    category: '',
    clothingType: '',
    jewelleryType: '',
    handycraftType: ''
  });
  const filterRef = useRef(null);
  const debounceRef = useRef(null);

  const loadListings = async (appliedFilters = {}, isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }
    
    try {
      // Clean up filters - remove empty values
      const cleanedFilters = {};
      Object.keys(appliedFilters).forEach(key => {
        if (appliedFilters[key] && appliedFilters[key] !== '') {
          cleanedFilters[key] = appliedFilters[key];
        }
      });

      const params = new URLSearchParams({
        limit: 8, // Show only 8 items for preview
        ...cleanedFilters
      });

      const response = await api.get(`/listings?${params}`);
      setListings(response.data.listings || []);
    } catch (error) {
      setListings([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setFilterLoading(false);
      }
    }
  };

  // Debounced filter function
  const debouncedLoadListings = useCallback((appliedFilters) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      loadListings(appliedFilters, false);
    }, 300); // 300ms debounce
  }, []);

  useEffect(() => {
    loadListings({}, true);
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      if (filterRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = filterRef.current;
        const scrollHint = document.getElementById('scroll-hint');
        
        if (scrollHeight > clientHeight) {
          filterRef.current.classList.add('has-scroll');
          if (scrollHint && scrollTop < 10) {
            scrollHint.classList.remove('hidden');
          }
        } else {
          filterRef.current.classList.remove('has-scroll');
          if (scrollHint) {
            scrollHint.classList.add('hidden');
          }
        }
      }
    };

    const handleScroll = () => {
      if (filterRef.current) {
        const scrollHint = document.getElementById('scroll-hint');
        if (scrollHint && filterRef.current.scrollTop > 10) {
          scrollHint.classList.add('hidden');
        }
      }
    };

    checkScroll();
    if (filterRef.current) {
      filterRef.current.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('resize', checkScroll);
    
    return () => {
      if (filterRef.current) {
        filterRef.current.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    
    // Clear related filters when changing item type
    if (name === 'itemType') {
      if (value === 'clothing') {
        newFilters.jewelleryType = '';
        newFilters.handycraftType = '';
      } else if (value === 'jewellery') {
        newFilters.clothingType = '';
        newFilters.handycraftType = '';
      } else if (value === 'handycraft') {
        newFilters.clothingType = '';
        newFilters.jewelleryType = '';
      }
    }
    
    setFilters(newFilters);
    debouncedLoadListings(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      itemType: '',
      category: '',
      clothingType: '',
      jewelleryType: '',
      handycraftType: ''
    };
    setFilters(clearedFilters);
    loadListings({}, false);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value && value !== '').length;
  };

  const trackView = async (itemId) => {
    try {
      const response = await api.post('/listings/track-view', { listingId: itemId });
    } catch (error) {
    }
  };

  const ItemCard = ({ item }) => {
    const handleCardClick = () => {
      trackView(item._id);
    };

    return (
      <div 
        className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20 card-hover cursor-pointer h-[400px] flex flex-col"
        onClick={handleCardClick}
      >
        <div className="relative">
          <img
            src={getImageUrl(item.images[0])}
            alt={item.itemName}
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
            }}
          />
          <div className="absolute top-3 right-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-lg badge-bounce pulse-effect ${
              (item.listingType || 'rent') === 'sell'
                ? 'bg-green-500 text-white'
                : item.availabilityStatus === 'available' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-orange-500 text-white'
            }`}>
              <i className={`mr-1 ${
                (item.listingType || 'rent') === 'sell'
                  ? 'fas fa-tag'
                  : item.availabilityStatus === 'available' 
                    ? 'fas fa-check-circle' 
                    : 'fas fa-calendar-times'
              }`}></i>
              {(item.listingType || 'rent') === 'sell'
                ? 'For Sale'
                : item.availabilityStatus === 'available' 
                  ? 'Available' 
                  : 'Currently Rented'
              }
            </span>
          </div>
          {/* Enhanced rental period display for rented items */}
          {item.availabilityStatus === 'rented' && item.rentalPeriod && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end">
              <div className="w-full p-3">
                <div className="bg-orange-500 text-white px-3 py-2 rounded-lg text-center shadow-lg">
                  <div className="flex items-center justify-center mb-1">
                    <i className="fas fa-calendar-alt mr-2"></i>
                    <span className="font-semibold text-sm">Currently Rented</span>
                  </div>
                  <div className="text-xs opacity-90">
                    {item.rentalPeriod.startDate} - {item.rentalPeriod.endDate}
                  </div>
                  <div className="text-xs font-medium mt-1">
                    <i className="fas fa-clock mr-1"></i>
                    Available in {item.rentalPeriod.daysRemaining} days
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className="bg-black/60 text-white px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
              <i className="fas fa-eye mr-1"></i>{item.views || 0} views
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: 'linear-gradient(to top, rgba(34, 40, 49, 0.8), transparent)' }}>
            <div className="text-white">
              <span className="text-xl font-bold">₹{item.price}</span>
              <span className="text-sm opacity-90 ml-1">
                {item.listingType === 'sell' ? '' : '/day'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-4 flex flex-col flex-grow">
          {/* Title Section - Fixed Height */}
          <div className="h-10 mb-3">
            <h3 className="font-bold text-lg text-gray-800 line-clamp-1">{item.itemName}</h3>
          </div>
          
          {/* Location and Size Section - Fixed Height */}
          <div className="h-6 flex items-center justify-between mb-3">
            <div className="flex items-center text-gray-600">
              <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
              <span className="text-sm">{item.location}</span>
            </div>
            <span className="text-white px-2 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#948979' }}>
              {item.itemType === 'jewellery' ? (item.jewellerySize || 'Free Size') : item.size}
            </span>
          </div>
          
          {/* Rental Timeline Section - Show for rented items */}
          {item.availabilityStatus === 'rented' && item.rentalPeriod && (
            <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center text-orange-700">
                  <i className="fas fa-calendar-check mr-1"></i>
                  <span className="font-medium">Rental Period</span>
                </div>
                <span className="text-orange-600 font-semibold">
                  {item.rentalPeriod.daysRemaining} days left
                </span>
              </div>
              <div className="text-xs text-orange-600 mt-1">
                {item.rentalPeriod.startDate} → {item.rentalPeriod.endDate}
              </div>
            </div>
          )}

          {/* Tags Section - Only Gender and Rent/Sell */}
          <div className="flex-grow mb-4">
            <div className="flex flex-wrap gap-2">
              {/* Rent/Sell Tag */}
              <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                item.listingType === 'sell' 
                  ? 'bg-orange-100 text-orange-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                <i className={`fas ${item.listingType === 'sell' ? 'fa-shopping-cart' : 'fa-handshake'} mr-1`}></i>
                {item.listingType === 'sell' ? 'For Sale' : 'For Rent'}
              </span>
              
              {/* Gender Tag */}
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium capitalize">
                <i className="fas fa-venus-mars mr-1"></i>{item.category || 'Handycraft'}
              </span>
            </div>
          </div>
          
          {/* Button Section - Bottom Aligned */}
          <div className="mt-auto pt-2">
            <Link 
              to="/login" 
              className={`w-full inline-block px-4 py-3 rounded-xl font-bold text-sm text-center transition-all duration-300 btn-stylish glow-effect transform hover:scale-105 shadow-lg ${
                item.availabilityStatus === 'rented' 
                  ? 'opacity-75' 
                  : ''
              }`}
              style={{ backgroundColor: '#222831', color: 'white' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
            >
              <i className="fas fa-sign-in-alt mr-2"></i>
              {item.availabilityStatus === 'rented' 
                ? 'Login to Check Availability' 
                : 'Login to Contact Owner'
              }
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id="browse" className="py-16 sm:py-20 md:py-24" style={{ backgroundColor: '#F6F1EB' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: '#222831' }}>
            Browse Available Items
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#393E46' }}>
            Discover amazing clothing and jewellery available for rent. Sign up to start chatting with owners!
          </p>
        </div>

        {/* Filters */}
        <div ref={filterRef} className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-white/20 max-h-96 overflow-y-auto filter-scroll relative">
          <div className="mb-4">
            <h3 className="text-lg font-semibold" style={{ color: '#222831' }}>
              <i className="fas fa-filter mr-2"></i>Filter Items
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: filters.itemType ? '#222831' : '#6B7280' }}>
                Item Type
                {filters.itemType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
              </label>
              <select 
                name="itemType"
                value={filters.itemType}
                onChange={handleFilterChange}
                className={`w-full border rounded-md px-3 py-2 transition-colors ${
                  filters.itemType 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <option value="">All Items</option>
                <option value="clothing">Clothing</option>
                <option value="jewellery">Jewellery</option>
                <option value="handycraft">Handy Craft</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: filters.category ? '#222831' : '#6B7280' }}>
                Category
                {filters.category && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
              </label>
              <select 
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className={`w-full border rounded-md px-3 py-2 transition-colors ${
                  filters.category 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <option value="">All Categories</option>
                <option value="men">Men</option>
                <option value="women">Women</option>
                <option value="unisex">Unisex</option>
              </select>
            </div>

            {(!filters.itemType || filters.itemType === 'clothing') && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: filters.clothingType ? '#222831' : '#6B7280' }}>
                  Clothing Type
                  {filters.clothingType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                </label>
                <select 
                  name="clothingType"
                  value={filters.clothingType}
                  onChange={handleFilterChange}
                  className={`w-full border rounded-md px-3 py-2 transition-colors ${
                    filters.clothingType 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="ethnic">Ethnic</option>
                  <option value="western">Western</option>
                  <option value="casual">Casual</option>
                  <option value="formal">Formal</option>
                  <option value="party">Party</option>
                  <option value="traditional">Traditional</option>
                </select>
              </div>
            )}

            {(!filters.itemType || filters.itemType === 'jewellery') && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: filters.jewelleryType ? '#222831' : '#6B7280' }}>
                  Jewellery Type
                  {filters.jewelleryType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                </label>
                <select 
                  name="jewelleryType"
                  value={filters.jewelleryType}
                  onChange={handleFilterChange}
                  className={`w-full border rounded-md px-3 py-2 transition-colors ${
                    filters.jewelleryType 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="necklace">Necklace</option>
                  <option value="earrings">Earrings</option>
                  <option value="bracelet">Bracelet</option>
                  <option value="ring">Ring</option>
                  <option value="anklet">Anklet</option>
                  <option value="bangles">Bangles</option>
                </select>
              </div>
            )}

            {(!filters.itemType || filters.itemType === 'handycraft') && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: filters.handycraftType ? '#222831' : '#6B7280' }}>
                  Handy Craft Type
                  {filters.handycraftType && <i className="fas fa-check-circle ml-1 text-green-500 text-xs"></i>}
                </label>
                <select 
                  name="handycraftType"
                  value={filters.handycraftType}
                  onChange={handleFilterChange}
                  className={`w-full border rounded-md px-3 py-2 transition-colors ${
                    filters.handycraftType 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="pottery">Pottery</option>
                  <option value="woodwork">Woodwork</option>
                  <option value="textiles">Textiles</option>
                  <option value="paintings">Paintings</option>
                  <option value="sculptures">Sculptures</option>
                  <option value="candles">Candles</option>
                  <option value="soaps">Handmade Soaps</option>
                  <option value="bags">Handmade Bags</option>
                  <option value="decorative">Decorative Items</option>
                  <option value="toys">Handmade Toys</option>
                  <option value="accessories">Craft Accessories</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}
          </div>
          
          {/* Filter Status and Actions */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getActiveFilterCount() > 0 && (
                  <span className="px-2 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#948979' }}>
                    {getActiveFilterCount()} active
                  </span>
                )}
                {filterLoading && (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-2 rounded-full animate-spin" style={{ borderTopColor: '#948979' }}></div>
                    <span className="ml-1 text-xs text-gray-500">Filtering...</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs px-3 py-1 rounded-full border transition-colors hover:bg-gray-50"
                    style={{ borderColor: '#948979', color: '#948979' }}
                  >
                    <i className="fas fa-times mr-1"></i>Clear All
                  </button>
                )}
                <div className="text-xs text-gray-500 hidden" id="scroll-hint">
                  <i className="fas fa-arrow-down mr-1"></i>Scroll for more
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Counter */}
        {!loading && (
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm" style={{ color: '#393E46' }}>
              {Array.isArray(listings) && listings.length > 0 ? (
                <>
                  Showing <span className="font-semibold">{Array.isArray(listings) ? listings.length : 0}</span> items
                  {getActiveFilterCount() > 0 && (
                    <span className="ml-1">
                      with <span className="font-semibold">{getActiveFilterCount()}</span> filter{getActiveFilterCount() > 1 ? 's' : ''} applied
                    </span>
                  )}
                </>
              ) : (
                'No items found'
              )}
            </div>
            {Array.isArray(listings) && listings.length > 0 && (
              <div className="text-xs text-gray-500">
                <i className="fas fa-info-circle mr-1"></i>
                Preview showing up to 8 items
              </div>
            )}
          </div>
        )}

        {/* Items Grid */}
        {loading ? (
          <div className="text-center py-16">
            <SimpleLoader size="xl" text="Loading items..." />
          </div>
        ) : (!Array.isArray(listings) || listings.length === 0) ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-search text-white text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>No items found</h3>
            <p className="mb-6 max-w-md mx-auto" style={{ color: '#393E46' }}>
              Try adjusting your filters or check back later for new items.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {listings.map(item => (
                <ItemCard key={item._id} item={item} />
              ))}
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>
                  Want to see more and start renting?
                </h3>
                <p className="mb-6" style={{ color: '#393E46' }}>
                  Join VestrioZ to browse all items, chat with owners, and start renting today!
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link 
                    to="/signup" 
                    className="px-6 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: '#222831', color: 'white' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                  >
                    <i className="fas fa-user-plus mr-2"></i>Sign Up Free
                  </Link>
                  <Link 
                    to="/login" 
                    className="px-6 py-3 rounded-xl font-bold border-2 transition-all duration-300"
                    style={{ borderColor: '#222831', color: '#222831' }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#222831';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#222831';
                    }}
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>Login
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicBrowse;